import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import parse from "html-react-parser";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import className from "./style.module.scss";

import { ControlContainer, SideBar } from "./Components";
import { ComparisonTabs, VegaPlot } from "@learningblocks/shared-components";

export function DataWidgetScene(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");

  const lang = i18next.language;
  const [loaded, setLoaded] = useState(false);
  const [dev, setDev] = useState(false);

  const [data, setData] = useState(props.sceneData);
  const [controls, setControls] = useState(props.sceneData.controls);
  const [controlData, setControlData] = useState({});
  const [sideBar, setSideBar] = useState(false);
  const [sideBarLoaded, setSideBarLoaded] = useState(false);

  useEffect(() => {
    // handle content translation if included
    if (props.translationData) {
      i18next.addResourceBundle(
        "en",
        "translation",
        props.translationData,
        true,
        true
      );
    }
    // handle component specific translation if included
    import(`./locales/${lang}/translation.json`).then((module) => {
      i18next.addResourceBundle("en", "translation", module.default, true, true);
      setLoaded(true);
    });

    // init control data (keys and values)
    const updatedControlData = controlData;
    data.controls.map((control) => {
      updatedControlData[control.variable_key] = control.default;
      setControlData({ ...updatedControlData });
    });

    // handle resize to determine controls side bar inclusion
    checkSideBarConditions();
    window.addEventListener("resize", checkSideBarConditions);
    return () => {
      window.removeEventListener("resize", checkSideBarConditions);
    };
  }, []);

  useEffect(() => {
    const updatedData = data;
    data.tabs.map((tab, index) => {
      if (tab.title === "Controls") {
        updatedData.tabs.splice(index, 1);
      }
    });
    if (!sideBar) {
      const controlTab = { title: "Controls" };
      updatedData.tabs.unshift(controlTab);
    }
    setData({ ...updatedData });
  }, [sideBar]);

  useEffect(() => {
    // handle project name if standalone (Move to project index!) // pin
    if (props.standalone) {
      document.title = `LabXchange | ${t(`${props.sceneData.title}.string`)}`;
    }
  }, [loaded]);

  const checkSideBarConditions = () => {
    setSideBarLoaded(false);
    const elem = document.getElementById(`dataWidgetScene_${data.id}`);
    if (elem.clientWidth > 375 * 2) {
      setSideBar(true);
      setSideBarLoaded(true);
    } else {
      setSideBar(false);
    }
  };

  const updateVariable = (variable_key, new_value, controlIndex) => {
    const updatedControlData = controlData;
    updatedControlData[variable_key] = new_value;
    setControlData({ ...updatedControlData });
    updateDefault(controlIndex, new_value);
  };

  const updateDefault = (index, new_value) => {
    const updatedControls = controls;
    updatedControls[index].default = new_value;
    setControls(updatedControls);
  };

  const getTabsContentComponents = (tabsData) => {
    const array = [];

    tabsData.map((tab, index) => {
      let elem;
      if (tab.title === "Controls") {
        elem = getControlTab(index);
      } else {
        elem = (
          <div
            key={index}
            className={className.TabContent}
            style={{
              paddingTop: tab.intro === "" ? "0px" : null,
            }}
          >
            {tab.intro !== "" ? (
              <p className={className.Intro}>
                {parse(t(`${tab.intro}.string`))}
              </p>
            ) : null}
            <div className={className.Plots}>
              {tab.plots.map((plot, key) => (
                <VegaPlot
                  key={key}
                  widgetID={data.id}
                  id={key}
                  plotData={plot}
                  plotCalculation={props.plotCalculations[plot.plotFunction]}
                  altTextCalculation={
                    props.plotCalculations[plot.altTextFunction]
                  }
                  controlData={controlData}
                  preview={props.preview}
                  dev={dev}
                />
              ))}
            </div>
          </div>
        );
      }
      array.push(elem);
    });

    return array;
  };

  const getControlTab = (index) => {
    return (
      <div
        key={index}
        className={className.TabContent}
        style={{
          paddingTop: props.controlTabIntro === "" ? "0px" : null,
        }}
      >
        {index !== 0 ? <h3>{parse(t(`Controls.string`))}</h3> : null}
        <p className={className.Intro}>
          {parse(t(`${data.controlTabIntro}.string`))}
        </p>
        <div className={className.Controls}>
          {data.controls.map((control, index) => {
            return (
              <ControlContainer
                key={index}
                control={control}
                index={index}
                updateVariable={updateVariable}
                dev={dev}
              />
            );
          })}
        </div>
      </div>
    );
  };

  if (!data) {
    return <pre>Loading...</pre>;
  }

  return (
    <div
      id={`dataWidgetScene_${data.id}`}
      className={className.DataWidgetScene}
      style={{
        display: sideBar ? "flex" : null,
        flexDirection: sideBar ? "row" : null,
      }}
    >
      {sideBar && sideBarLoaded && loaded ? (
        <SideBar>{getControlTab()}</SideBar>
      ) : null}
      <div
        className={className.ComparisonTabsContainer}
        style={{ maxWidth: sideBar ? "calc(100% - 375px)" : null }}
      >
        <ComparisonTabs
          tabsData={data.tabs}
          tabsContentComponents={getTabsContentComponents(data.tabs)}
          widgetID={data.id}
          tabMaxWidth={375}
        >
          {getTabsContentComponents(data.tabs).map((tab) => tab)}
        </ComparisonTabs>
      </div>

      {props.preview === true && data.dev === true ? (
        <div>
          <div
            className={className.DevToggle}
            role="button"
            tabIndex="0"
            onClick={() => {
              setDev(!dev);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setDev(!dev);
              }
            }}
          >
            Dev: {dev ? "On" : "Off"}
          </div>
          {props.sceneData.recordURL !== undefined && dev ? (
            <div
              className={className.DevURL}
              role="link"
              tabIndex="0"
              onClick={() => {
                window.open(props.sceneData.recordURL, "_blank");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  window.open(props.sceneData.recordURL, "_blank");
                }
              }}
            >
              Airtable record
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

DataWidgetScene.propTypes = {
  sceneData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    controls: PropTypes.array.isRequired,
    tabs: PropTypes.array.isRequired,
  }).isRequired,
  dev: PropTypes.bool,
  preview: PropTypes.bool,
  standalone: PropTypes.bool,
  translationData: PropTypes.object,
  plotCalculations: PropTypes.object.isRequired,
};
DataWidgetScene.defaultProps = {
  controls: [],
  tabs: [],
  dev: false,
  preview: false,
  standalone: false,
};
